import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
} from '@angular/core';
import { faRedo, faUndo } from '@fortawesome/free-solid-svg-icons';
import { assert } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { getShapesForEventTracking } from '../../../../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import { EventTrackingValueWithSource } from '../../../../../../cross-team-shared/misc-utils';
import { outputAnnotationInfoStoreToken } from '../../../../../../outputs/annotation/annotation-shared/annotation-providers';
import { isShapeLayer } from '../../../../../../outputs/annotation/annotation-shared/state/layers/layer-utils';
import { AbstractAnnotationStore } from '../../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { AnnotationState } from '../../../../../../outputs/annotation/annotation-shared/state/root/annotation-state';
import { EventTrackingService } from '../../../../../../shared/event-tracking/event-tracking.service';
import { EventTrackingValue } from '../../../../../../team-apis/core/event-tracking';
import { FusionState } from '../../../../../h3ml/state/fusion-state';
import { fusionStoreToken } from '../../../../../h3ml/state/task-workspace-store';
import { ShapeLayerState } from '../../state/layers/shape-layer-state';
import { ObjectlessFocusedShapeService } from '../../utils/objectless-focused-shape.service';
import { OutputKeyboardShortcutService } from '../../utils/output-keyboard-shortcut.service';
import { UndoService } from './undo.service';

@Component({
  selector: 'h3-undo-redo-buttons',
  templateUrl: './undo-redo-buttons.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UndoRedoButtonsComponent extends DirectiveSuperclass {
  private readonly shapesStore = inject(outputAnnotationInfoStoreToken)(
    'shapes',
  );
  private readonly fusionStore = inject(fusionStoreToken);

  faUndo = faUndo;
  faRedo = faRedo;

  constructor(
    public undoService: UndoService,
    private keyboardShortcutService: OutputKeyboardShortcutService,
    private eventTrackingService: EventTrackingService,
    private store: AbstractAnnotationStore,
    private focusedShapeService: ObjectlessFocusedShapeService,
    injector: Injector,
  ) {
    super(injector);
    this.registerKeyboardShortcuts();
  }

  undo(eventTrackingValue: EventTrackingValueWithSource) {
    if (!this.undoService.canUndo()) {
      return;
    }

    const stateBefore = this.store.state();
    const fusionStateBefore = this.fusionStore.state();
    this.undoService.undo();
    this.trackEvents(
      'undo',
      eventTrackingValue,
      stateBefore,
      fusionStateBefore,
    );
  }

  redo(eventTrackingValue: EventTrackingValue) {
    if (!this.undoService.canRedo()) {
      return;
    }

    const stateBefore = this.store.state();
    const fusionStateBefore = this.fusionStore.state();
    this.undoService.redo();
    this.trackEvents(
      'redo',
      eventTrackingValue,
      stateBefore,
      fusionStateBefore,
    );
  }

  private registerKeyboardShortcuts() {
    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('undo'),
      ({ keyboardEvent, eventTrackingValue }) => {
        keyboardEvent.preventDefault(); // prevent browser undo
        if (this.undoService.canUndo()) {
          this.undo(eventTrackingValue);
        }
      },
    );
    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('redo'),
      ({ keyboardEvent, eventTrackingValue }) => {
        keyboardEvent.preventDefault(); // prevent browser redo
        if (this.undoService.canRedo()) {
          this.redo(eventTrackingValue);
        }
      },
    );
  }

  private trackEvents(
    undoOrRedo: 'undo' | 'redo',
    eventTrackingValue: EventTrackingValue,
    stateBefore: AnnotationState,
    fusionStateBefore: FusionState,
  ) {
    this.eventTrackingService.track({
      id: undoOrRedo,
      bucket: 'adjustment',
      ...eventTrackingValue,
    });

    const shapeLayerIndex = stateBefore.layers.findIndex(isShapeLayer);

    if (shapeLayerIndex >= 0) {
      const shapeLayerAfter = this.store.state().layers[shapeLayerIndex];
      assert(
        isShapeLayer(shapeLayerAfter),
        'Shape layer type should never change',
      );

      this.trackTagEditorEventIfVisibilityChanges(
        // type checked above, ok to cast
        stateBefore.layers[shapeLayerIndex] as ShapeLayerState,
        shapeLayerAfter,
        fusionStateBefore,
        undoOrRedo,
      );
    }
  }

  private trackTagEditorEventIfVisibilityChanges(
    shapeLayerBefore: ShapeLayerState,
    shapeLayerAfter: ShapeLayerState,
    fusionStateBefore: FusionState,
    undoOrRedo: 'undo' | 'redo',
  ) {
    const fusionStateAfter = this.fusionStore.state();
    const { wasVisible, willBeVisible } = this.rightPanelWasOrWillBeVisible(
      shapeLayerBefore,
      shapeLayerAfter,
      fusionStateBefore,
      fusionStateAfter,
    );

    const trackingValues = {
      bucket: 'tag_selection',
      sub_bucket: 'annotation',
      source: undoOrRedo,
    };

    // Tracking two separate events for a single user action is not our
    // normal way of handling events, but it's necessary here because of
    // DataDen requirements.
    if (wasVisible && !willBeVisible) {
      this.eventTrackingService.track({
        id: 'close_tag_editor',
        ...trackingValues,
      });
    } else if (!wasVisible && willBeVisible) {
      this.trackShowTagEditorEvent(trackingValues);
    }
  }

  private rightPanelWasOrWillBeVisible(
    shapeLayerBefore: ShapeLayerState,
    shapeLayerAfter: ShapeLayerState,
    fusionStateBefore: FusionState,
    fusionStateAfter: FusionState,
  ) {
    const previousTransient = shapeLayerBefore.transient;
    const newTransient = shapeLayerAfter.transient;
    const wasVisible =
      !previousTransient.tagEditorHidden &&
      fusionStateBefore.isRightPanelExpanded;
    const willBeVisible =
      !newTransient.tagEditorHidden && fusionStateAfter.isRightPanelExpanded;

    return { wasVisible, willBeVisible };
  }

  private trackShowTagEditorEvent(trackingValues: {
    bucket: string;
    source: 'undo' | 'redo';
  }) {
    const index = this.focusedShapeService.getFocusedShapeIndex();

    if (!index) {
      throw new Error('There should be a focused shape.');
    }

    const shapesForEventTracking = getShapesForEventTracking(
      // @ts-ignore -- fix me
      this.shapesStore.state(),
      [index],
    );
    this.eventTrackingService.track({
      id: 'show_tag_editor',
      shapes: shapesForEventTracking,
      ...trackingValues,
    });
  }
}
